'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function SifyVoiceoverCaseStudy() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(43,98,239,0.06), transparent, rgb(8,11,18))' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <Link href="/case-studies" className="inline-flex items-center gap-2 text-cobalt text-sm font-medium mb-8 hover:text-cobalt-glow transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Case Studies
          </Link>
          <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">Voiceover</span>
          <h1 className="font-display font-bold text-4xl lg:text-5xl text-silver-text leading-tight mb-6 max-w-4xl">
            Sify Digital Multilingual eLearning Voiceover
          </h1>
          <p className="text-lg text-silver-muted max-w-2xl leading-relaxed mb-10">
            18-language eLearning voiceover delivery with 97% first-delivery acceptance, 50% faster timelines, and 99% on-time delivery - consistent audio quality across a wide set of languages and voice profiles.
          </p>
          <div className="flex flex-wrap gap-6">
            {[
              { value: '18', label: 'Languages' },
              { value: '97%', label: 'First Delivery Acceptance' },
              { value: '50% Faster', label: 'Timeline Reduction' },
              { value: '99%', label: 'On-Time Delivery' },
            ]?.map((stat) => (
              <div key={stat?.label} className="glow-card rounded-xl px-6 py-4 text-center min-w-[120px]">
                <div className="text-xl font-bold text-cobalt-glow">{stat?.value}</div>
                <div className="text-xs text-silver-muted mt-1">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">

              {/* Challenge */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">01</span>
                  The Challenge
                </h2>
                <p className="text-silver-muted leading-relaxed mb-4">
                  Sify Digital needed multilingual voiceover for global eLearning courses with tight delivery windows, high linguistic accuracy, and consistent audio quality across a wide set of languages and voice profiles.
                </p>
                <p className="text-silver-muted leading-relaxed">
                  The project required voiceovers in 18 languages with 4 voice options per language and a turnaround time of 3 days per language - covering technical training, compliance, software tutorials, and employee onboarding content.
                </p>
              </div>

              {/* Deliverables */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">02</span>
                  Content Types Delivered
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: 'Technical Training', desc: 'Complex technical content requiring precise terminology and clear narration' },
                    { title: 'Compliance Training', desc: 'Regulatory and compliance modules requiring formal, accurate delivery' },
                    { title: 'Software Tutorials', desc: 'Step-by-step software instruction with consistent pacing and clarity' },
                    { title: 'Employee Onboarding', desc: 'Engaging onboarding content across multiple languages and regional accents' },
                  ]?.map((item) => (
                    <div key={item?.title} className="glow-card rounded-xl p-5">
                      <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                      <p className="text-xs text-silver-muted">{item?.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Production Process */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">03</span>
                  Voiceover Production Process
                </h2>
                <div className="space-y-4">
                  {[
                    { step: '1', title: 'Script Analysis & Preparation', desc: 'Review of source scripts for linguistic nuance, technical terminology, and pronunciation requirements.' },
                    { step: '2', title: 'Translation & Localization', desc: 'Professional translation adapted for spoken delivery, accounting for timing, tone, and regional dialect.' },
                    { step: '3', title: 'Voice Talent Selection', desc: '4 voice options per language presented to client, with regional accent and gender preferences accommodated.' },
                    { step: '4', title: 'Recording & Direction', desc: 'Professional studio recording with real-time direction to ensure consistent quality and pacing.' },
                    { step: '5', title: 'Post-Production', desc: 'Audio editing, noise reduction, level normalization, and timing alignment with eLearning modules.' },
                    { step: '6', title: 'Final Review & Delivery', desc: 'Native linguist review of final audio against scripts, followed by delivery in required formats.' },
                  ]?.map((item) => (
                    <div key={item?.step} className="flex gap-4 p-5 glow-card rounded-xl">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-bold">{item?.step}</div>
                      <div>
                        <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                        <p className="text-xs text-silver-muted leading-relaxed">{item?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">04</span>
                  Language Coverage
                </h2>
                <div className="flex flex-wrap gap-2">
                  {['French', 'German', 'Spanish', 'Italian', 'Portuguese (Brazil)', 'Dutch', 'Polish', 'Russian', 'Japanese', 'Korean', 'Chinese (Mandarin)', 'Thai', 'Vietnamese', 'Indonesian', 'Hindi', 'Arabic', 'Turkish', 'English (US)']?.map((lang) => (
                    <span key={lang} className="px-3 py-1.5 rounded-lg bg-cobalt/10 border border-cobalt/20 text-cobalt-glow text-xs font-mono">{lang}</span>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">05</span>
                  Results
                </h2>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {[
                    { metric: '97% first delivery acceptance', detail: 'Across all 18 languages' },
                    { metric: '50% timeline reduction', detail: 'Compared to previous voiceover workflows' },
                    { metric: '99% on-time delivery', detail: 'All modules delivered within agreed windows' },
                    { metric: '3 days per language', detail: 'Rapid turnaround with 4 voice options per language' },
                  ]?.map((r) => (
                    <div key={r?.metric} className="glow-card rounded-xl p-5 border-l-2 border-cobalt/40">
                      <div className="font-semibold text-cobalt-glow text-sm mb-1">{r?.metric}</div>
                      <div className="text-xs text-silver-muted">{r?.detail}</div>
                    </div>
                  ))}
                </div>
                <blockquote className="border-l-2 border-cobalt/40 pl-5 py-2">
                  <p className="text-silver-muted italic text-sm leading-relaxed mb-2">"Exceptional quality and consistency across all languages."</p>
                  <cite className="text-cobalt-glow text-xs not-italic">Ajith Menon, VP of Delivery, Sify Digital</cite>
                </blockquote>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-4">Project Details</h3>
                <dl className="space-y-3 text-sm">
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Client</dt><dd className="text-silver-text">Sify Digital</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Industry</dt><dd className="text-silver-text">eLearning / Voiceover</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Languages</dt><dd className="text-silver-text">18 languages</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Voice Options</dt><dd className="text-silver-text">4 per language</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Turnaround</dt><dd className="text-silver-text">3 days per language</dd></div>
                </dl>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Key Capabilities Used</h3>
                <ul className="space-y-2 text-sm text-silver-muted">
                  {['Script translation for voiceover', 'Voice talent sourcing', 'Professional studio recording', 'Post-production audio editing', 'Native linguist review', 'Multi-language project management', 'eLearning format delivery']?.map((cap) => (
                    <li key={cap} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-cobalt-glow flex-shrink-0" />
                      {cap}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Need Multilingual Voiceover?</h3>
                <p className="text-sm text-silver-muted mb-4">Talk to our team about your eLearning voiceover program.</p>
                <Link href="/get-a-quote">
                  <span className="block text-center px-4 py-3 bg-cobalt hover:bg-cobalt-glow text-white text-sm font-semibold rounded-lg transition-all">
                    Get a Quote
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
